/**
 * Client-side port of the API's calendar end-date calculation so commits can
 * submit timeRanges[].endDate. The commit endpoint (POST schedules/{id}/commits)
 * persists endDate verbatim and never derives it — the API only recomputes end
 * dates on calendar publish (CalendarCalculationService.AdjustTaskEndDate), so
 * a payload without endDate stays endDate-less until the next publish.
 *
 * Every function mirrors a backend counterpart (CoreWebApi):
 *  - calculateTaskEndDate      -> CalendarCalculationService.CalculateTaskEndDate
 *  - isWorkingAt / intervalCovers / recurringWindowContains -> same-named privates
 *    (folded here into resolveDayWorkingRanges, which resolves a whole day at
 *    once instead of one instant at a time — see below)
 *  - parseCron5                -> Models/ProgressMonitoring/Cron5.cs
 *  - toDurationWeightingHours  -> TimeRangeDurationHelper.ToDurationWeightingFromDisplay
 *
 * Implementation: a day-window walk that is byte-for-byte equivalent to the
 * server's minute-by-minute walk, only far cheaper. The server steps a cursor
 * one minute at a time through calendar-local wall-clock time, calling
 * IsWorkingAt (a scan of every interval) at each step; consuming one whole
 * minute of duration per working step, then rolling forward to the next working
 * minute and converting back to UTC. Ported literally that is O(minutes) with a
 * full interval scan per minute — minutes with dense holiday calendars — so a
 * multi-hundred-day rollup blocks the main thread for seconds.
 *
 * We keep the exact grid the server evaluates — the instants
 * t_k = startLocal + k*60000 for k = 0, 1, 2, … — but observe that the walk only
 * ever needs two facts about it:
 *   1. how many of those instants are "working" (each consumes one whole minute,
 *      so W working minutes consume ceil(W) working instants), and
 *   2. the location of the next working instant after the last consumed one
 *      (that instant, converted local->UTC, is the answer).
 * So the end date is simply the (ceil(W) + 1)-th working grid-instant. We find
 * it by resolving each calendar day once into its merged working ranges (a
 * boundary sweep with the same highest-priority-wins / first-in-list tie rule
 * IsWorkingAt uses), then counting grid-instants inside those ranges with pure
 * integer arithmetic on k — never stepping minute by minute. Prepared intervals
 * and per-day range resolutions are memoised per calendar object (a WeakMap), so
 * a commit run that reuses one calendar across ~2000 tasks resolves each shared
 * day at most once.
 *
 * Semantics kept identical: the walk lives in calendar-local wall-clock ms,
 * where every day is exactly 24h and DST only surfaces at the UTC<->local
 * conversions on the boundaries; inclusive-start/exclusive-end intervals;
 * highest-priority interval wins (ties: first in list); no covering interval
 * means non-working; cron day patterns use OR when both day-of-month and
 * day-of-week are restricted; a recurring end at or before its start rolls to
 * the next day (overnight shifts, and an equal start/end therefore spans a full
 * day). The results are verified ms-exact against the old minute walk by a
 * differential test.
 *
 * Known divergences (both fail safe by returning null, i.e. omit endDate):
 *  - An empty/unknown timeZone falls back to UTC here; the server falls back to
 *    its own local zone. Windows ids (what RDrive calendars carry) are mapped
 *    to IANA via timeZoneIds.js, matching the server's FindSystemTimeZoneById.
 *  - A calendar with no working time makes the server loop forever; here the
 *    walk gives up after MAX_WALK_MINUTES.
 */

import { toIanaTimeZone } from '@/utils/timeZoneIds';

const MINUTE_MS = 60 * 1000;
const DAY_MS = 24 * 60 * MINUTE_MS;

// Working-time conversion fallbacks (TimeRangeDurationHelper 8/5/20).
const DEFAULT_HOURS_PER_DAY = 8;
const DEFAULT_DAYS_PER_WEEK = 5;
const DEFAULT_DAYS_PER_MONTH = 20;

// ~10 years of minutes; backstop against calendars whose intervals never yield
// a working minute after the start date.
const MAX_WALK_MINUTES = 10 * 366 * 24 * 60;

const timeZoneFormatters = new Map();
const offsetCache = new Map();

function getFormatter(timeZone) {
  let formatter = timeZoneFormatters.get(timeZone);
  if (formatter !== undefined) return formatter;
  const iana = toIanaTimeZone(timeZone);
  formatter = iana
    ? new Intl.DateTimeFormat('en-US', {
      timeZone: iana,
      hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    })
    : null; // unknown id (neither IANA nor Windows) — caller falls back to UTC
  timeZoneFormatters.set(timeZone, formatter);
  return formatter;
}

/**
 * The published calendar a timeRange is walked/displayed against — the same
 * resolution the commit walk and the API's publish-time recalculation use:
 * the range's calendarId, falling back to the current revision of the same
 * version family (the store only holds current published calendars, so a
 * range stamped with an older revision id still resolves).
 * @param {Array} calendars - published calendars (scheduleStore.getCalendars)
 * @param {{calendarId?: string, calendarVersionReference?: string}} timeRange
 * @returns {object|null}
 */
export function resolvePublishedCalendar(calendars, timeRange) {
  if (!timeRange) return null;
  const list = calendars || [];
  const byId = timeRange.calendarId ? list.find(c => c.id === timeRange.calendarId) : null;
  if (byId) return byId;
  const ref = timeRange.calendarVersionReference;
  return (ref && list.find(c => c.versionReference === ref)) || null;
}

/**
 * Offset (ms) to add to a UTC instant to get calendar-local wall-clock time.
 * Cached per (timeZone, minute); lookups only happen at conversion boundaries
 * (start, fixed-interval bounds, result), not during the day walk.
 */
function getTimeZoneOffsetMs(timeZone, utcMs) {
  if (!timeZone) return 0;
  const formatter = getFormatter(timeZone);
  if (!formatter) return 0;

  const cacheKey = `${timeZone}:${Math.floor(utcMs / MINUTE_MS)}`;
  const cached = offsetCache.get(cacheKey);
  if (cached !== undefined) return cached;

  const parts = {};
  formatter.formatToParts(new Date(utcMs)).forEach(({ type, value }) => {
    parts[type] = value;
  });
  const wallClockMs = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour) % 24, // Intl can emit "24" at midnight
    Number(parts.minute),
    Number(parts.second)
  );
  const offset = wallClockMs - Math.floor(utcMs / 1000) * 1000;
  offsetCache.set(cacheKey, offset);
  return offset;
}

function utcToLocalMs(timeZone, utcMs) {
  return utcMs + getTimeZoneOffsetMs(timeZone, utcMs);
}

/**
 * Wall-clock -> UTC. Two-pass refinement handles the offset changing between
 * the naive guess and the actual instant (DST edges).
 */
function localToUtcMs(timeZone, localMs) {
  let utcGuess = localMs - getTimeZoneOffsetMs(timeZone, localMs);
  utcGuess = localMs - getTimeZoneOffsetMs(timeZone, utcGuess);
  return localMs - getTimeZoneOffsetMs(timeZone, utcGuess);
}

/**
 * Parse one cron field ("*", "3", "1-5", "1,3,5", "1-3,5") into
 * { star, values:Set }. Returns null when invalid (Cron5.Field.Parse).
 */
function parseCronField(text, min, max) {
  if (text === '*') return { star: true, values: new Set() };
  const values = new Set();
  for (const token of text.split(',')) {
    const t = token.trim();
    if (t === '') continue;
    if (t.includes('-')) {
      const range = t.split('-').filter(Boolean);
      if (range.length !== 2) return null;
      let a = Number(range[0]);
      let b = Number(range[1]);
      if (!Number.isInteger(a) || !Number.isInteger(b)) return null;
      if (a > b) [a, b] = [b, a];
      if (a < min || b > max) return null;
      for (let i = a; i <= b; i++) values.add(i);
    } else {
      const v = Number(t);
      if (!Number.isInteger(v) || v < min || v > max) return null;
      values.add(v);
    }
  }
  if (values.size === 0) return null;
  return { star: false, values };
}

/**
 * 5-field cron: "minute hour day-of-month month day-of-week". Minute and hour
 * must be concrete numbers; day-of-week 7 normalizes to Sunday (0).
 * Returns null when invalid — the interval then never covers, instead of
 * throwing mid-walk like the server would on unvalidated data.
 */
export function parseCron5(cron) {
  if (!cron || typeof cron !== 'string') return null;
  const fields = cron.trim().split(/\s+/);
  if (fields.length !== 5) return null;

  const minute = Number(fields[0]);
  const hour = Number(fields[1]);
  if (!Number.isInteger(minute) || minute < 0 || minute > 59) return null;
  if (!Number.isInteger(hour) || hour < 0 || hour > 23) return null;

  const dom = parseCronField(fields[2], 1, 31);
  const month = parseCronField(fields[3], 1, 12);
  const dowRaw = parseCronField(fields[4], 0, 7);
  if (!dom || !month || !dowRaw) return null;

  let dow = dowRaw;
  if (!dowRaw.star) {
    const values = new Set();
    dowRaw.values.forEach(v => values.add(v === 7 ? 0 : v));
    dow = { star: false, values };
  }
  return { minute, hour, dom, month, dow };
}

/**
 * Cron5.MatchesDay: month must match; when both day-of-month and day-of-week
 * are restricted the match is OR, otherwise the restricted one must match.
 * localDayMs is midnight of the day in question, read as UTC parts of the
 * epoch-shifted wall-clock value.
 */
function cronMatchesDay(cron, localDayMs) {
  const d = new Date(localDayMs);
  if (!cron.month.star && !cron.month.values.has(d.getUTCMonth() + 1)) return false;

  const domMatch = cron.dom.star || cron.dom.values.has(d.getUTCDate());
  const dowMatch = cron.dow.star || cron.dow.values.has(d.getUTCDay());

  if (!cron.dom.star && !cron.dow.star) return domMatch || dowMatch;
  return domMatch && dowMatch;
}

/**
 * RecurringWindowContains expressed as bounds: the concrete [start, end) local-ms
 * window a recurring interval opens on the calendar day beginning at dayMs, or
 * null when the day does not match the start cron (no window). Mirrors the
 * server's RecurringWindowContains: window = [day + startHH:MM, day + endHH:MM),
 * and an end at or before the start rolls forward one day — so overnight shifts
 * spill past midnight, and a marker whose start == end spans a full 24h.
 */
function recurringWindowBounds(startCron, endCron, dayMs) {
  if (!cronMatchesDay(startCron, dayMs)) return null;

  const start = dayMs + (startCron.hour * 60 + startCron.minute) * MINUTE_MS;
  let end = dayMs + (endCron.hour * 60 + endCron.minute) * MINUTE_MS;
  if (end <= start) end += DAY_MS;

  return { start, end };
}

/**
 * Resolve each interval once per calendar: parse crons, convert fixed UTC bounds
 * to local wall-clock. Unusable intervals (missing bounds, bad cron) drop out,
 * mirroring IntervalCovers returning false for them; survivors keep their
 * relative order so first-in-list tie-breaks match the server's stable sort.
 */
function prepareIntervals(calendar) {
  const timeZone = calendar.timeZone || '';
  return (calendar.intervals || [])
    .map(interval => {
      if (!interval) return null;
      const isRecurring = !!interval.recurrentStartDate;
      const base = {
        isWorking: interval.isWorking !== false,
        priority: Number.isFinite(interval.priority) ? interval.priority : 20
      };
      if (!isRecurring) {
        if (!interval.startDate || !interval.endDate) return null;
        const startUtc = Date.parse(interval.startDate);
        const endUtc = Date.parse(interval.endDate);
        if (Number.isNaN(startUtc) || Number.isNaN(endUtc)) return null;
        return {
          ...base,
          fixed: true,
          localStartMs: utcToLocalMs(timeZone, startUtc),
          localEndMs: utcToLocalMs(timeZone, endUtc)
        };
      }
      const startCron = parseCron5(interval.recurrentStartDate);
      const endCron = parseCron5(interval.recurrentEndDate);
      if (!startCron || !endCron) return null;
      return { ...base, fixed: false, startCron, endCron };
    })
    .filter(Boolean);
}

// Prepared intervals + per-day resolutions, memoised per calendar object. The
// commit importer passes the same store calendar for every task in a run, so
// every task shares this cache and each calendar day is resolved at most once.
const preparedCache = new WeakMap();

function getPrepared(calendar) {
  let entry = preparedCache.get(calendar);
  if (entry === undefined) {
    const intervals = prepareIntervals(calendar);
    entry = {
      intervals,
      hasWorking: intervals.some(interval => interval.isWorking),
      dayCache: new Map()
    };
    preparedCache.set(calendar, entry);
  }
  return entry;
}

/**
 * Clip [window.start, window.end) to [dayStart, dayEnd) and, if anything
 * remains, push it as a covering segment tagged with its owning interval's
 * list index (for tie-breaks), priority and isWorking flag.
 */
function pushSegment(segments, window, dayStart, dayEnd, idx, priority, isWorking) {
  if (window === null) return;
  const start = window.start > dayStart ? window.start : dayStart;
  const end = window.end < dayEnd ? window.end : dayEnd;
  if (start < end) segments.push({ start, end, idx, priority, isWorking });
}

/**
 * Resolve one calendar day (day index D in local-ms space, dayStart = D*DAY_MS)
 * into a sorted, disjoint, merged list of working [startLocalMs, endLocalMs)
 * ranges. This is the batch form of the server's IsWorkingAt: instead of asking
 * "is this one instant working?" a minute at a time, we resolve the whole day
 * once and get identical answers for every instant in it.
 *
 * A boundary sweep does the work. Each interval contributes the portion of the
 * day it covers as a segment; between two consecutive segment bounds coverage is
 * constant, so a single winner lookup per sub-range decides the whole sub-range.
 * The winner is the highest-priority covering segment, ties broken by earliest
 * position in the prepared list (matching IsWorkingAt's strict `>` / stable
 * sort), and the sub-range is working iff a winner exists and it isWorking.
 */
function resolveDayWorkingRanges(preparedIntervals, dayIndex) {
  const dayStart = dayIndex * DAY_MS;
  const dayEnd = dayStart + DAY_MS;

  // 1. Collect covering segments clipped to [dayStart, dayEnd).
  const segments = [];
  for (let idx = 0; idx < preparedIntervals.length; idx++) {
    const interval = preparedIntervals[idx];
    if (interval.fixed) {
      pushSegment(
        segments,
        { start: interval.localStartMs, end: interval.localEndMs },
        dayStart, dayEnd, idx, interval.priority, interval.isWorking
      );
    } else {
      // A recurring interval reaches into day D two ways, exactly the two-term OR
      // in the server's IntervalCovers: its own window on day D, and the previous
      // day's window spilling past midnight (an overnight shift).
      pushSegment(
        segments,
        recurringWindowBounds(interval.startCron, interval.endCron, dayStart),
        dayStart, dayEnd, idx, interval.priority, interval.isWorking
      );
      pushSegment(
        segments,
        recurringWindowBounds(interval.startCron, interval.endCron, dayStart - DAY_MS),
        dayStart, dayEnd, idx, interval.priority, interval.isWorking
      );
    }
  }
  if (segments.length === 0) return [];

  // 2. Breakpoints = every distinct segment bound; coverage is constant between
  //    consecutive breakpoints.
  const bounds = [];
  for (const seg of segments) {
    bounds.push(seg.start);
    bounds.push(seg.end);
  }
  bounds.sort((a, b) => a - b);

  // 3. For each elementary sub-range [lo, hi) pick the winner and, when it is a
  //    working sub-range, append it — merging with the previous range when they
  //    touch so the output is maximal and disjoint.
  const ranges = [];
  for (let i = 0; i + 1 < bounds.length; i++) {
    const lo = bounds[i];
    const hi = bounds[i + 1];
    if (lo >= hi) continue; // duplicate breakpoint

    let winner = null;
    for (const seg of segments) {
      if (seg.start > lo || lo >= seg.end) continue; // does not cover [lo, hi)
      if (winner === null
        || seg.priority > winner.priority
        || (seg.priority === winner.priority && seg.idx < winner.idx)) {
        winner = seg;
      }
    }
    if (winner === null || !winner.isWorking) continue; // non-working sub-range

    const last = ranges.length > 0 ? ranges[ranges.length - 1] : null;
    if (last !== null && last.end === lo) last.end = hi; // extend contiguous work
    else ranges.push({ start: lo, end: hi });
  }
  return ranges;
}

function getDayWorkingRanges(entry, dayIndex) {
  let ranges = entry.dayCache.get(dayIndex);
  if (ranges === undefined) {
    ranges = resolveDayWorkingRanges(entry.intervals, dayIndex);
    entry.dayCache.set(dayIndex, ranges);
  }
  return ranges;
}

/**
 * Exact integer ceil(numerator / divisor) for divisor > 0 and safe-integer
 * inputs. We repair any one-ulp slip in the floating-point division so the
 * grid-instant counts below are never off by one.
 */
function ceilDiv(numerator, divisor) {
  let q = Math.floor(numerator / divisor);
  if (numerator - q * divisor < 0) q -= 1;
  else if (numerator - q * divisor >= divisor) q += 1;
  return numerator === q * divisor ? q : q + 1;
}

/**
 * TimeRangeDurationHelper.ToDurationWeightingFromDisplay — display duration
 * (value + unit) to canonical working hours using the calendar's conversion
 * baselines. Returns null for unknown units (the server throws there, but it
 * validates units before ever converting).
 */
export function toDurationWeightingHours(value, unit, calendar) {
  const safeValue = !Number.isFinite(value) || value < 0 ? 0 : value;
  const hoursPerDay = calendar?.hoursPerDay > 0 ? calendar.hoursPerDay : DEFAULT_HOURS_PER_DAY;
  const daysPerWeek = calendar?.daysPerWeek > 0 ? calendar.daysPerWeek : DEFAULT_DAYS_PER_WEEK;
  const daysPerMonth = calendar?.daysPerMonth > 0 ? calendar.daysPerMonth : DEFAULT_DAYS_PER_MONTH;

  switch ((unit || '').toLowerCase()) {
    case 'minute': return safeValue / 60;
    case 'hour': return safeValue;
    case 'day': return safeValue * hoursPerDay;
    case 'week': return safeValue * daysPerWeek * hoursPerDay;
    case 'month': return safeValue * daysPerMonth * hoursPerDay;
    case 'year': return safeValue * 12 * daysPerMonth * hoursPerDay;
    default: return null;
  }
}

/**
 * CalendarCalculationService.CalculateTaskEndDate: consume `workingMinutes` of
 * working time from `startUtcMs` in calendar-local time, then land on the next
 * working minute and convert back to UTC. Returns epoch ms, or null when the
 * walk cannot finish (no working time reachable / invalid inputs).
 *
 * The server steps minute by minute; we reach the identical instant by counting
 * grid-instants inside each day's resolved working ranges. On the grid
 * t_k = startLocal + k*60000 (k >= 0) the server consumes one working instant
 * per whole minute of duration — so ceil(workingMinutes) working instants are
 * consumed — then rolls forward to the very next working instant. That instant
 * is therefore the (ceil(workingMinutes) + 1)-th working grid-instant, and it,
 * converted local->UTC, is the answer.
 */
export function calculateTaskEndDateMs(calendar, startUtcMs, workingMinutes) {
  if (!calendar || !Number.isFinite(startUtcMs) || !Number.isFinite(workingMinutes) || workingMinutes < 0) {
    return null;
  }
  if (workingMinutes === 0) return startUtcMs;

  const prepared = getPrepared(calendar);
  if (!prepared.hasWorking) return null;

  const timeZone = calendar.timeZone || '';
  const startLocal = utcToLocalMs(timeZone, startUtcMs);

  // Index (1-based) of the working grid-instant we must return: consume
  // ceil(workingMinutes), then take the next one. (workingMinutes > 0 here, so
  // the count matches the server's fractional final-minute consumption exactly.)
  const need = Math.ceil(workingMinutes) + 1;

  // The server's minute walk gives up once its cursor has advanced more than
  // MAX_WALK_MINUTES from the start; that cursor equals startLocal + k*60000 at
  // grid-instant k, so any answer with k > MAX_WALK_MINUTES (equivalently, any
  // instant past this local time) must instead be null. We stop scanning days
  // once they start beyond that horizon.
  const lastRelevantLocal = startLocal + MAX_WALK_MINUTES * MINUTE_MS;

  let found = 0;
  let dayIndex = Math.floor(startLocal / DAY_MS);
  for (;;) {
    const dayStart = dayIndex * DAY_MS;
    if (dayStart > lastRelevantLocal) return null; // walked past the backstop

    const ranges = getDayWorkingRanges(prepared, dayIndex);
    for (let r = 0; r < ranges.length; r++) {
      const a = ranges[r].start;
      const b = ranges[r].end;

      // Number of grid-instants t_k in [a, b) with k >= 0, by integer math on k:
      // firstK = first k with t_k >= a (never below 0), afterK = first k with
      // t_k >= b. All instants in this range are k in [firstK, afterK).
      const firstK = Math.max(0, ceilDiv(a - startLocal, MINUTE_MS));
      const afterK = ceilDiv(b - startLocal, MINUTE_MS);
      if (afterK <= firstK) continue; // no grid-instant lands in this range

      const count = afterK - firstK;
      if (found + count >= need) {
        // The instant we owe is the (need - found)-th working instant of this
        // range, at grid index targetK.
        const targetK = firstK + (need - found - 1);
        if (targetK > MAX_WALK_MINUTES) return null; // beyond the server backstop
        return localToUtcMs(timeZone, startLocal + targetK * MINUTE_MS);
      }
      found += count;
    }
    dayIndex += 1;
  }
}

/**
 * Inverse of calculateTaskEndDateMs: the number of working grid-instants
 * t_k = startLocal + k*60000 (k >= 0) inside [startLocal, endLocal) — i.e.
 * the working minutes a walk starting at `startUtcMs` must consume to land on
 * `endUtcMs`. Feeding the result back into calculateTaskEndDateMs reproduces
 * `endUtcMs` exactly when it is itself a working grid-instant (any prior
 * walk's output is), and otherwise lands on the next working instant after
 * it — the same forward snap the server walk applies. Returns null for
 * invalid input or a calendar with no working time; 0 for a non-positive
 * span.
 */
export function calculateWorkingMinutesBetweenMs(calendar, startUtcMs, endUtcMs) {
  if (!calendar || !Number.isFinite(startUtcMs) || !Number.isFinite(endUtcMs)) return null;
  if (endUtcMs <= startUtcMs) return 0;

  const prepared = getPrepared(calendar);
  if (!prepared.hasWorking) return null;

  const timeZone = calendar.timeZone || '';
  const startLocal = utcToLocalMs(timeZone, startUtcMs);
  const endLocal = utcToLocalMs(timeZone, endUtcMs);
  if (endLocal <= startLocal) return 0;

  let count = 0;
  for (let dayIndex = Math.floor(startLocal / DAY_MS); dayIndex * DAY_MS < endLocal; dayIndex++) {
    const ranges = getDayWorkingRanges(prepared, dayIndex);
    for (let r = 0; r < ranges.length; r++) {
      const a = Math.max(ranges[r].start, startLocal);
      const b = Math.min(ranges[r].end, endLocal);
      if (b <= a) continue;
      const firstK = Math.max(0, ceilDiv(a - startLocal, MINUTE_MS));
      const afterK = ceilDiv(b - startLocal, MINUTE_MS);
      if (afterK > firstK) count += afterK - firstK;
    }
  }
  return count;
}

/**
 * End date for one timeRange ({ startDate, duration: { value, unit } }) as an
 * ISO string, or null when it cannot be computed. Working minutes are derived
 * from value + unit + calendar (weighting * 60), exactly how the commit
 * endpoint normalizes duration.weighting server-side.
 */
export function computeTimeRangeEndDate(timeRange, calendar) {
  if (!timeRange || !timeRange.startDate || !timeRange.duration) return null;

  const startUtcMs = Date.parse(timeRange.startDate);
  if (Number.isNaN(startUtcMs)) return null;

  const weightingHours = toDurationWeightingHours(
    Number(timeRange.duration.value),
    timeRange.duration.unit,
    calendar
  );
  if (weightingHours === null) return null;

  const endMs = calculateTaskEndDateMs(calendar, startUtcMs, weightingHours * 60);
  return endMs === null ? null : new Date(endMs).toISOString();
}
